import { Button, Col, Row } from 'react-bootstrap'
import { useMutation } from 'react-query'
import { IoWarningOutline } from 'react-icons/io5'
import { FaCheckCircle } from 'react-icons/fa'
import { useBreakpoint } from '../../../hooks'

import useContainerContext from '../../containerContext'
import { DocumentHeader, HeaderButton, VehicleItem } from '../../shared'
import GroupButton from './groupButton'
import ModalPhotoList from '../../ModalPhotoList'
import ModalReceipt from '../../paymentInfo/receipt'
import ModalPaymentInfo from "../../paymentInfo"
import useAppContext from '../../app'
import ModalReject from './ModalReject'
import ModalDisclosures from './ModalDisclosures'
import { useApi, useLoading } from '../../../hooks'
import toast from '../../toast'
import { VEHICLE_STATUS } from '../../../core/constants1'

const EstimateDetail = () => {
	const { store: { isLog }} = useAppContext();
	const { data, state, showPhotoModal, closeModalPhoto, approveOrRejectVehicle, closeModalVerify, closeModalDisclosure, closeModalDeposit, closeModalProcessingCard, toApproveEst } = useContainerContext();
	const { showModalPhoto, vehicle, initialSlide, isPayment, isReceipt, isShowPopupVerify, isShowModalDisclosure, isShowModalDeposit, isShowModalProcessingCard } = state;
	const { company = {}, vehicles = [], hasSupplement = false, client: { _id: client = {} }, status } = data;
	const isShowButtonApproveOrReject = status;
	// Every vehicle decided AND at least one approved AND at least one vehicle.
	// The at-least-one-approved term is load-bearing, not defensive: the API's
	// work-order build filters on status === 'approved', so a zero-approved
	// convert silently creates nothing. Derived at render time and never latched
	// -- VehicleItem lets an approved vehicle be flipped to declined.
	//
	// The decided term is an ALLOW-list, not `!== UNDECIDED`. The old exclusion
	// test was true for a vehicle carrying no status key at all, so a legacy
	// vehicle nobody ever decided on could be carried into an approved estimate
	// alongside an approved one. Matching on the two decided values fails closed
	// for undefined and for any value that is not canonical.
	const canApproveEstimate = vehicles.length > 0
		&& vehicles.every(vehicle => vehicle.status === VEHICLE_STATUS.APPROVED
			|| vehicle.status === VEHICLE_STATUS.DECLINED)
		&& vehicles.some(vehicle => vehicle.status === VEHICLE_STATUS.APPROVED);
	const { BREAKPOINT, screenBiggerThan } = useBreakpoint();
	const isBiggerThanMD = screenBiggerThan(BREAKPOINT.MD);
	const isBiggerThanLG = screenBiggerThan(BREAKPOINT.LG);

	let isShowSupplement = hasSupplement;
	let isShowPdf = true;
	let isShowParts = !!(vehicles.filter(({ parts }) => parts?.length).length);
	let isShowPayDeposit = true;
	let showAdasChecklists = false;
	let hasDisclosure = false;
	let showScans = true;
	let lang;
	if (!isLog) {
		const token = localStorage['access_token'];
		const payload = token ? JSON.parse(window.atob(token.split('.')[1])) : null;
		isShowPdf = payload?.pdf;
		isShowPayDeposit = payload?.deposit;
		showAdasChecklists = payload?.adasChecklists;
		hasDisclosure = payload?.hasDisclosure;
		showScans = payload?.scans !== false;
		lang = payload?.language;
	}

	const companyProps = (() => {
		if (client.group && client.group.companyOverride && client.group.companyOverride.active) return client.group.companyOverride;
		return company;
	})();
	const companyLogo = companyProps.logo || company?.logo || null;

	const { pdfService } = useApi();
	const { isLoading, mutate: downloadPdf } = useMutation(
		params => pdfService.estimatePDF(params, false),
		{ onError: error => toast.error({ title: 'View PDF errors', body: error.message }) }
	);
	useLoading(isLoading);

	const handleApproveOrRejectVehicle = async (vehicle) => {
		// US 27042: on a single-vehicle estimate, approving the one vehicle IS
		// approving the estimate, so carry straight on into the approve flow.
		// Derived entirely from click-time inputs -- the vehicle COUNT, which a status
		// write cannot change, and the status being requested -- so there is nothing
		// stale to read. That matters here: the mutation's onSuccess mutates the
		// vehicle objects in place and discards the server's recalculated totals.
		const shouldAutoAdvance = vehicles.length === 1
			&& vehicle.status === VEHICLE_STATUS.APPROVED;

		try {
			// mutateAsync, not mutate: it settles after onSuccess has written the
			// cache. It also REJECTS on error, hence the catch -- without it this
			// would be an unhandled rejection.
			await approveOrRejectVehicle.mutateAsync({
				estimateId: data._id,
				vehicleId: vehicle._id,
				status: vehicle.status
			});
		} catch (error) {
			return; // the mutation's onError already toasted
		}

		// toApproveEst, not showSignature: it owns the deposit gate, the
		// company/client rule checks and the disclosures branch, none of which the
		// single-vehicle shortcut may bypass.
		if (shouldAutoAdvance) toApproveEst();
	}

	const getStatusBadge = () => {
		if (status) {
			return (
				<Button
					variant='warning'
					className='fs-small text-white fw-bold py-1 px-3 rounded-pill d-flex align-items-center'
					style={{ pointerEvents: 'none' }}
				>
					<IoWarningOutline size={16} className='me-1' />
					Needs Approval
				</Button>
			);
		}
		return (
			<Button
				variant='success'
				className='fs-small text-white fw-bold py-1 px-3 rounded-pill d-flex align-items-center'
				style={{ pointerEvents: 'none' }}
			>
				<FaCheckCircle size={16} className='me-1' />
				Approved
			</Button>
		);
	}

	return (
		<div className='my-5 px-sm-3'>
			<Row>
				<Col sm={10} md={8} xl={5} lg={6} className='mx-auto'>
					<GroupButton isBiggerThanMD={isBiggerThanMD} isBiggerThanLG={isBiggerThanLG} isShowPayDeposit={isShowPayDeposit} />
				</Col>
			</Row>

			<Row>
				<Col md={12}>
					<div className='pt-4'>
						<DocumentHeader
							documentType="Estimate"
							documentNumber={data.number}
							dateCreated={data.dateCreated}
							createdBy={data.createdBy?.name}
							total={data.total || 0}
							currency={data.currency}
							client={client}
							company={companyProps}
							companyLogo={companyLogo}
							onBack={() => window.history.back()}
							statusBadge={getStatusBadge()}
							buttons={
								<>
									{status && (
										<HeaderButton
							variant="outline"
							onClick={toApproveEst}
							disabled={!canApproveEstimate}
						>
											APPROVE ESTIMATE
										</HeaderButton>
									)}
									{isShowPdf && (
										<HeaderButton variant="primary" onClick={() => downloadPdf({ recordId: data._id, hasDisclosure, lang })}>
											VIEW PDF
										</HeaderButton>
									)}
								</>
							}
						/>

						<div className='mt-3'>
							{vehicles.map((vehicle, index) => (
								<VehicleItem
									record={data}
									key={vehicle._id}
									isEstimateDetail={true}
									index={index}
									vehicle={vehicle}
									onShowPhoto={showPhotoModal}
									isShowSupplement={isShowSupplement}
									isShowParts={isShowParts}
									showEstAdasChecklists={showAdasChecklists}
									handleApproveOrRejectVehicle={handleApproveOrRejectVehicle}
									isShowButtonApproveOrReject={isShowButtonApproveOrReject}
									showScans={showScans}
								/>
							))}
						</div>
					</div>

					{showModalPhoto && (
						<ModalPhotoList vehicle={vehicle} initialSlide={initialSlide} onClose={closeModalPhoto} isInvoice={false} />
					)}
					{isReceipt && <ModalReceipt isInvoice={false} />}
					{isPayment && <ModalPaymentInfo data={data} isInvoice={false} />}
					{isShowModalDeposit && <ModalReject isShow={isShowModalDeposit} closeModal={closeModalDeposit} title="SORRY..." body="Deposit payment required before estimate can be approved." />}
					{isShowModalProcessingCard && <ModalReject isShow={isShowModalProcessingCard} closeModal={closeModalProcessingCard} title="SORRY…" body="The online payment hasn't supported by the company yet. Please contact the company for further support!" />}

					<ModalReject
						isShow={isShowPopupVerify}
						closeModal={closeModalVerify}
						title="OOPS..."
						body="There are some required fields which must be filled by Technicians. Please contact the technicians accordingly."
					/>
					{isShowModalDisclosure && <ModalDisclosures isShow={isShowModalDisclosure} closeModal={closeModalDisclosure} />}
				</Col>
			</Row>
		</div>
	)
}
export default EstimateDetail
